export const VIEWPORT_DB_NAME = 'nula-canvas-viewport';
const STORE = 'viewports';
const INDEX = 'byUser';

export interface ViewportEntry {
  canvasId: string;
  userId: string;
  x: number;
  y: number;
  zoom: number;
  lastUsedAt: number;
}

// Module-level singleton connection — opened once, shared across calls.
// Exposed via getter for testability (close + null to reset between tests).
let _db: IDBDatabase | null = null;

export function openViewportDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VIEWPORT_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      const store = db.createObjectStore(STORE, { keyPath: ['canvasId', 'userId'] });
      store.createIndex(INDEX, 'userId');
    };
    req.onsuccess = () => {
      _db = req.result;
      resolve(_db);
    };
    req.onerror = () => reject(req.error);
  });
}

/** Close the singleton connection. Required in tests before deleteDatabase. */
export function closeViewportDb(): void {
  if (_db) {
    _db.close();
    _db = null;
  }
}

export async function getViewport(
  canvasId: string,
  userId: string,
): Promise<ViewportEntry | null> {
  const db = await openViewportDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get([canvasId, userId]);
    req.onsuccess = () => resolve((req.result as ViewportEntry | undefined) ?? null);
    tx.onerror = () => reject(tx.error);
  });
}

export async function setViewport(
  canvasId: string,
  userId: string,
  v: { x: number; y: number; zoom: number },
  lastUsedAt: number = Date.now(),
): Promise<void> {
  const db = await openViewportDb();
  const entry: ViewportEntry = { canvasId, userId, ...v, lastUsedAt };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function evictOld(userId: string, max: number = 200): Promise<void> {
  const db = await openViewportDb();

  // Collect all entries for this user via the index.
  const entries: ViewportEntry[] = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).index(INDEX).getAll(IDBKeyRange.only(userId));
    req.onsuccess = () => resolve((req.result ?? []) as ViewportEntry[]);
    tx.onerror = () => reject(tx.error);
  });

  if (entries.length <= max) return;

  entries.sort((a, b) => a.lastUsedAt - b.lastUsedAt);
  const toEvict = entries.slice(0, entries.length - max);

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    for (const e of toEvict) {
      store.delete([e.canvasId, e.userId]);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
